import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Clock3, Link2, Mail, MapPin, Phone, Plus, Search, Trash2, UserRound, Users, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authFetch from '../../utils/authFetch';
import { resolveContactConflictResponse } from '../../utils/contactResolution';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const createEmptyContact = () => ({
  fullName: '',
  dateOfBirth: '',
  hasMet: false,
  description: '',
  address: '',
  email: '',
  phone: '',
  notes: ''
});

const toEditableContact = (contact) => ({
  fullName: contact?.fullName || '',
  dateOfBirth: contact?.dateOfBirth ? String(contact.dateOfBirth).slice(0, 10) : '',
  hasMet: Boolean(contact?.hasMet),
  description: contact?.description || '',
  address: contact?.address || '',
  email: contact?.email || '',
  phone: contact?.phone || '',
  notes: contact?.notes || ''
});

const CONTACT_CHANNEL_FILTERS = {
  all: 'All',
  phone: 'Phone',
  email: 'Email'
};

const MET_FILTERS = {
  all: 'All',
  met: 'Met',
  not_met: "Haven't Met"
};

const MOBILE_BREAKPOINT = 900;

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('sl-SI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateOnly = (value) => {
  if (!value) return 'N/A';

  if (typeof value === 'string') {
    const isoMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('sl-SI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const ACTIVITY_SOURCE_LABELS = {
  property_lead_owner: 'Lead Gen owner',
  property_lead: 'Lead Gen lead',
  selling_property_owner: 'Selling owner',
  selling_property_contact: 'Selling contact',
  zk_owner_aggregate: 'ZK owner',
  manual_contact: 'Manual contact',
  cold_calling_lead: 'Cold calling',
  property_showing: 'Showing',
  showing_owner: 'Showing owner'
};

const ACTIVITY_FIELD_LABELS = {
  full_name: 'Name',
  description: 'Description',
  address: 'Address',
  email: 'Email',
  phone: 'Phone',
  notes: 'Notes',
  has_met: 'Met status',
  katastrska_obcina: 'Cadastral municipality',
  id_parcele_stavbe: 'Parcel/building ID',
  owner_names: 'Owner names',
  zk_date: 'ZK date',
  type: 'Type',
  interactions: 'Interactions'
};

const formatActivitySource = (sourceType) => ACTIVITY_SOURCE_LABELS[sourceType] || String(sourceType || 'System').replace(/_/g, ' ');

const formatActivityField = (fieldName) => ACTIVITY_FIELD_LABELS[fieldName] || String(fieldName || 'field').replace(/_/g, ' ');

const formatActivityValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Empty';
  }

  const stringValue = String(value);
  try {
    const parsed = JSON.parse(stringValue);
    if (Array.isArray(parsed)) {
      return parsed.length === 0 ? 'Empty' : parsed.map((item) => JSON.stringify(item)).join(' | ');
    }
    if (parsed && typeof parsed === 'object') {
      return JSON.stringify(parsed);
    }
  } catch (err) {
  }

  return stringValue;
};

const readResponsePayload = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return {
    error: text.startsWith('<!DOCTYPE') ? 'Server returned an HTML error page.' : text
  };
};

const ContactValue = ({ icon, label, value, onIconClick, iconTitle, active }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
    {onIconClick ? (
      <button
        type="button"
        onClick={onIconClick}
        title={iconTitle}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '10px',
          backgroundColor: active ? '#dbeafe' : '#eff6ff',
          border: active ? '1px solid #93c5fd' : '1px solid transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#2563eb',
          cursor: 'pointer',
          flex: '0 0 auto'
        }}
      >
        {icon}
      </button>
    ) : (
      <div style={{ width: '32px', height: '32px', borderRadius: '10px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', flex: '0 0 auto' }}>
        {icon}
      </div>
    )}
    <div>
      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '14px', color: '#0f172a', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{value || 'N/A'}</div>
    </div>
  </div>
);

const ModalField = ({ label, children }) => (
  <label style={{ display: 'grid', gap: '6px' }}>
    <span style={{ fontSize: '12px', color: '#64748b' }}>{label}</span>
    {children}
  </label>
);

const filterChipStyle = (active) => ({
  padding: '8px 12px',
  borderRadius: '999px',
  border: active ? '1px solid #0f766e' : '1px solid #cbd5e1',
  backgroundColor: active ? '#ecfeff' : 'white',
  color: active ? '#0f766e' : '#475569',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer'
});

const metBadgeStyle = (hasMet) => ({
  padding: '4px 8px',
  borderRadius: '999px',
  backgroundColor: hasMet ? '#dcfce7' : '#dbeafe',
  color: hasMet ? '#166534' : '#1d4ed8',
  fontSize: '11px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  textTransform: 'lowercase'
});

const sendWithContactResolution = async (makeRequest, initialPayload) => {
  let payload = initialPayload;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const response = await makeRequest(payload);
    if (response.ok) {
      return response;
    }

    const conflict = await resolveContactConflictResponse(response, payload);
    if (!conflict.shouldRetry) {
      return response;
    }

    payload = conflict.payload;
  }

  return makeRequest(payload);
};

const ContactsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const copyFeedbackTimeoutRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [activity, setActivity] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [updatingContact, setUpdatingContact] = useState(false);
  const [updatingMetState, setUpdatingMetState] = useState(false);
  const [deletingContact, setDeletingContact] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [error, setError] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [newContact, setNewContact] = useState(createEmptyContact());
  const [editContact, setEditContact] = useState(createEmptyContact());
  const [noteDraft, setNoteDraft] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [metFilter, setMetFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [showMobileDetails, setShowMobileDetails] = useState(false);
  const [copiedContactField, setCopiedContactField] = useState('');
  const requestedContactId = Number(searchParams.get('contactId')) || null;

  const clearRequestedContactId = useCallback(() => {
    if (!searchParams.get('contactId')) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('contactId');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setShowMobileDetails(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => () => {
    if (copyFeedbackTimeoutRef.current) {
      window.clearTimeout(copyFeedbackTimeoutRef.current);
    }
  }, []);

  const refreshSelectedContact = useCallback(async (contactId) => {
    if (!contactId) return;

    const [detailResponse, activityResponse] = await Promise.all([
      authFetch(`${API_BASE}/api/contacts/${contactId}`),
      authFetch(`${API_BASE}/api/contacts/${contactId}/activity`)
    ]);

    if (!detailResponse.ok || !activityResponse.ok) {
      throw new Error('Failed to load contact detail');
    }

    const [detailData, activityData] = await Promise.all([
      detailResponse.json(),
      activityResponse.json()
    ]);

    setSelectedContact(detailData);
    setActivity(activityData);
  }, []);

  const fetchContacts = useCallback(async () => {
    setLoadingList(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      const url = `${API_BASE}/api/contacts${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error('Failed to load contacts');
      }
      const data = await response.json();
      setContacts(data);
      setSelectedContactId((currentSelectedContactId) => {
        if (data.length === 0) {
          setSelectedContact(null);
          setActivity([]);
          return null;
        }

        if (currentSelectedContactId && data.some((item) => item.id === currentSelectedContactId)) {
          return currentSelectedContactId;
        }

        return data[0].id;
      });
    } catch (err) {
      console.error(err);
      setError('Could not load contacts.');
    } finally {
      setLoadingList(false);
    }
  }, [search]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    const fetchContactDetail = async () => {
      if (!selectedContactId) return;
      setLoadingDetail(true);
      try {
        await refreshSelectedContact(selectedContactId);
      } catch (err) {
        console.error(err);
        setError('Could not load selected contact.');
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchContactDetail();
  }, [refreshSelectedContact, selectedContactId]);

  const displayedContacts = contacts
    .filter((contact) => {
      if (channelFilter === 'phone' && !contact.phone) return false;
      if (channelFilter === 'email' && !contact.email) return false;
      if (metFilter === 'met' && !contact.hasMet) return false;
      if (metFilter === 'not_met' && contact.hasMet) return false;
      return true;
    })
    .sort((left, right) => {
      const leftName = String(left.fullName || '').trim();
      const rightName = String(right.fullName || '').trim();
      if (!leftName && !rightName) return left.id - right.id;
      if (!leftName) return 1;
      if (!rightName) return -1;
      return leftName.localeCompare(rightName, 'sl', { sensitivity: 'base' });
    });

  useEffect(() => {
    if (displayedContacts.length === 0) {
      setSelectedContactId(null);
      setSelectedContact(null);
      setActivity([]);
      return;
    }

    if (!displayedContacts.some((contact) => contact.id === selectedContactId)) {
      setSelectedContactId(displayedContacts[0].id);
    }
  }, [contacts, channelFilter, metFilter, selectedContactId]);

  useEffect(() => {
    if (!requestedContactId) {
      return;
    }

    if (contacts.some((contact) => contact.id === requestedContactId) && selectedContactId !== requestedContactId) {
      setSelectedContactId(requestedContactId);
      if (isMobile) {
        setShowMobileDetails(true);
      }
    }
  }, [contacts, isMobile, requestedContactId, selectedContactId]);

  const handleCreateContact = async () => {
    if (!newContact.fullName.trim()) {
      alert('Full name is required.');
      return;
    }

    setSavingContact(true);
    setError('');
    try {
      const payload = {
        fullName: newContact.fullName.trim(),
        dateOfBirth: newContact.dateOfBirth || null,
        hasMet: Boolean(newContact.hasMet),
        description: newContact.description.trim() || null,
        address: newContact.address.trim() || null,
        email: newContact.email.trim() || null,
        phone: newContact.phone.trim() || null,
        notes: newContact.notes.trim() || null
      };

      const response = await sendWithContactResolution(
        (nextPayload) => authFetch(`${API_BASE}/api/contacts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextPayload)
        }),
        payload
      );

      const result = await readResponsePayload(response);
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create contact');
      }

      setIsAddModalOpen(false);
      setNewContact(createEmptyContact());
      await fetchContacts();
      if (result.id) {
        setSelectedContactId(result.id);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not create contact.');
    } finally {
      setSavingContact(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!selectedContact) return;
    setEditContact(toEditableContact(selectedContact));
    setIsEditModalOpen(true);
  };

  const handleSaveContact = async () => {
    if (!selectedContact) return;
    if (!editContact.fullName.trim()) {
      alert('Full name is required.');
      return;
    }

    setUpdatingContact(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/contacts/${selectedContact.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: editContact.fullName.trim(),
          dateOfBirth: editContact.dateOfBirth || null,
          hasMet: Boolean(editContact.hasMet),
          description: editContact.description.trim() || null,
          address: editContact.address.trim() || null,
          email: editContact.email.trim() || null,
          phone: editContact.phone.trim() || null,
          notes: editContact.notes.trim() || null
        })
      });
      const result = await readResponsePayload(response);
      if (!response.ok) {
        throw new Error(result.error || 'Failed to update contact');
      }

      setContacts((prev) => prev.map((contact) => (
        contact.id === selectedContact.id ? { ...contact, ...result } : contact
      )));
      await refreshSelectedContact(selectedContact.id);
      setIsEditModalOpen(false);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not update contact.');
    } finally {
      setUpdatingContact(false);
    }
  };

  const handleMetStateChange = async (nextHasMet) => {
    if (!selectedContact || selectedContact.hasMet === nextHasMet) return;

    setUpdatingMetState(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/contacts/${selectedContact.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hasMet: nextHasMet })
      });
      const result = await readResponsePayload(response);
      if (!response.ok) {
        throw new Error(result.error || 'Failed to update contact');
      }

      setContacts((prev) => prev.map((contact) => (
        contact.id === selectedContact.id ? { ...contact, hasMet: nextHasMet } : contact
      )));
      await refreshSelectedContact(selectedContact.id);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not update met status.');
    } finally {
      setUpdatingMetState(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    if (!window.confirm(`Delete contact "${selectedContact.fullName || 'Unnamed contact'}"?`)) {
      return;
    }

    setDeletingContact(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/contacts/${selectedContact.id}`, {
        method: 'DELETE'
      });
      const result = await readResponsePayload(response);
      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete contact');
      }

      const deletedId = selectedContact.id;
      const remainingContacts = contacts.filter((contact) => contact.id !== deletedId);
      setContacts(remainingContacts);
      setActivity([]);
      setSelectedContact(null);
      setSelectedContactId(remainingContacts[0]?.id || null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not delete contact.');
    } finally {
      setDeletingContact(false);
    }
  };

  const handleSelectContact = (contactId) => {
    clearRequestedContactId();
    setSelectedContactId(contactId);
    if (isMobile) {
      setShowMobileDetails(true);
    }
  };

  const handleCopyContactField = async (event, contactId, fieldName, value) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!value) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      const feedbackKey = `${contactId}:${fieldName}`;
      setCopiedContactField(feedbackKey);
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
      copyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setCopiedContactField('');
      }, 1400);
    } catch (err) {
      console.error('Failed to copy contact field:', err);
      setError(`Could not copy ${fieldName}.`);
    }
  };

  const handleAddNote = async () => {
    if (!selectedContact) return;
    const note = noteDraft.trim();
    if (!note) {
      alert('Note is required.');
      return;
    }

    setAddingNote(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/contacts/${selectedContact.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
      const result = await readResponsePayload(response);
      if (!response.ok) {
        throw new Error(result.error || 'Failed to add note');
      }

      await refreshSelectedContact(selectedContact.id);
      setNoteDraft('');
      setIsNoteModalOpen(false);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not add note.');
    } finally {
      setAddingNote(false);
    }
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <button
        onClick={() => navigate('/real-estate')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, color: '#64748b', cursor: 'pointer', fontSize: '14px', marginBottom: '12px' }}
      >
        <ArrowLeft size={16} /> Back to Real Estate Hub
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={26} /> Contacts
          </h1>
          <p style={{ color: '#64748b', margin: '8px 0 0', fontSize: '14px' }}>
            Unified contact registry across lead gen, cold calling, showings, and aggregated ZK owners.
          </p>
        </div>

        <div style={{ display: 'grid', gap: '12px', justifyItems: isMobile ? 'stretch' : 'end', flex: '1 1 520px' }}>
          <div style={{ minWidth: '280px', width: '100%', maxWidth: '420px', position: 'relative' }}>
            <Search size={16} color="#94a3b8" style={{ position: 'absolute', top: '12px', left: '12px' }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, email, or phone"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 38px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(CONTACT_CHANNEL_FILTERS).map(([value, label]) => (
                <button key={value} onClick={() => setChannelFilter(value)} style={filterChipStyle(channelFilter === value)}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(MET_FILTERS).map(([value, label]) => (
                <button key={value} onClick={() => setMetFilter(value)} style={filterChipStyle(metFilter === value)}>
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#0f766e', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              <Plus size={16} /> Add Contact
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '10px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#991b1b', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 420px) minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
        {(!isMobile || !showMobileDetails) && (
        <div style={{ backgroundColor: 'white', borderRadius: isMobile ? '0' : '16px', border: isMobile ? 'none' : '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#0f172a', fontSize: '16px', fontWeight: 600 }}>All Contacts</div>
            <div style={{ color: '#64748b', fontSize: '12px' }}>{displayedContacts.length} shown</div>
          </div>

          {loadingList ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>Loading contacts...</div>
          ) : displayedContacts.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No contacts found.</div>
          ) : (
            <div style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
              {displayedContacts.map((contact) => {
                const selected = contact.id === selectedContactId;
                const copiedPhone = copiedContactField === `${contact.id}:phone`;
                const copiedEmail = copiedContactField === `${contact.id}:email`;
                return (
                  <div
                    key={contact.id}
                    onClick={() => handleSelectContact(contact.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelectContact(contact.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      textAlign: 'left',
                      padding: isMobile ? '16px 18px' : '14px 18px',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: selected ? '#eff6ff' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', minWidth: 0 }}>
                      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                        <div style={{ color: '#0f172a', fontSize: '14px', fontWeight: 600 }}>{contact.fullName || 'Unnamed contact'}</div>
                        <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', wordBreak: 'break-word' }}>
                          {contact.email || contact.phone || 'No direct contact info'}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                          DOB: {formatDateOnly(contact.dateOfBirth)}
                        </div>
                      </div>
                      <div style={{ display: 'grid', justifyItems: 'end', gap: '8px' }}>
                        <div style={metBadgeStyle(contact.hasMet)}>{contact.hasMet ? 'met' : "haven't met"}</div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={(event) => handleCopyContactField(event, contact.id, 'phone', contact.phone)}
                            disabled={!contact.phone}
                            title={contact.phone ? `Copy phone: ${contact.phone}` : 'No phone number'}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '999px',
                              border: copiedPhone ? '1px solid #16a34a' : '1px solid #cbd5e1',
                              backgroundColor: copiedPhone ? '#dcfce7' : 'white',
                              color: contact.phone ? (copiedPhone ? '#166534' : '#475569') : '#cbd5e1',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: contact.phone ? 'pointer' : 'not-allowed',
                              opacity: contact.phone ? 1 : 0.55
                            }}
                          >
                            <Phone size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={(event) => handleCopyContactField(event, contact.id, 'email', contact.email)}
                            disabled={!contact.email}
                            title={contact.email ? `Copy email: ${contact.email}` : 'No email'}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '999px',
                              border: copiedEmail ? '1px solid #2563eb' : '1px solid #cbd5e1',
                              backgroundColor: copiedEmail ? '#dbeafe' : 'white',
                              color: contact.email ? (copiedEmail ? '#1d4ed8' : '#475569') : '#cbd5e1',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: contact.email ? 'pointer' : 'not-allowed',
                              opacity: contact.email ? 1 : 0.55
                            }}
                          >
                            <Mail size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {(!isMobile || showMobileDetails) && (
        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: isMobile ? '0' : '16px', border: isMobile ? 'none' : '1px solid #e2e8f0', padding: '20px' }}>
            {loadingDetail ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>Loading contact detail...</div>
            ) : !selectedContact ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>Select a contact to view details.</div>
            ) : (
              <>
                {isMobile && (
                  <button
                    onClick={() => setShowMobileDetails(false)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, color: '#64748b', cursor: 'pointer', fontSize: '14px', marginBottom: '16px' }}
                  >
                    <ArrowLeft size={16} /> Back to contacts
                  </button>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '20px' }}>
                  <div>
                    <h2 style={{ margin: 0, color: '#0f172a', fontSize: '24px' }}>{selectedContact.fullName || 'Unnamed contact'}</h2>
                    <div style={{ marginTop: '8px', color: '#64748b', fontSize: '13px' }}>
                      Primary source: {selectedContact.primarySource || 'N/A'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleOpenEditModal}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '10px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}
                    >
                      Edit Contact
                    </button>
                    <button
                      onClick={() => setIsNoteModalOpen(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '10px', border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#a16207', cursor: 'pointer' }}
                    >
                      Note
                    </button>
                    <button
                      onClick={handleDeleteContact}
                      disabled={deletingContact}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '10px', border: '1px solid #fecaca', backgroundColor: '#fff1f2', color: '#b91c1c', cursor: deletingContact ? 'not-allowed' : 'pointer', opacity: deletingContact ? 0.65 : 1 }}
                    >
                      <Trash2 size={14} /> {deletingContact ? 'Deleting...' : 'Delete Contact'}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  <ContactValue label="Date of Birth" value={formatDateOnly(selectedContact.dateOfBirth)} icon={<UserRound size={16} />} />
                  <ContactValue
                    label="Address"
                    value={selectedContact.address}
                    icon={<MapPin size={16} />}
                    onIconClick={selectedContact.address ? (event) => handleCopyContactField(event, 'detail', 'address', selectedContact.address) : null}
                    iconTitle={selectedContact.address ? `Copy address: ${selectedContact.address}` : 'No address'}
                    active={copiedContactField === 'detail:address'}
                  />
                  <ContactValue
                    label="Email"
                    value={selectedContact.email}
                    icon={<Mail size={16} />}
                    onIconClick={selectedContact.email ? (event) => handleCopyContactField(event, 'detail', 'email', selectedContact.email) : null}
                    iconTitle={selectedContact.email ? `Copy email: ${selectedContact.email}` : 'No email'}
                    active={copiedContactField === 'detail:email'}
                  />
                  <ContactValue
                    label="Phone"
                    value={selectedContact.phone}
                    icon={<Phone size={16} />}
                    onIconClick={selectedContact.phone ? (event) => handleCopyContactField(event, 'detail', 'phone', selectedContact.phone) : null}
                    iconTitle={selectedContact.phone ? `Copy phone: ${selectedContact.phone}` : 'No phone'}
                    active={copiedContactField === 'detail:phone'}
                  />
                  <ContactValue label="Created" value={formatDateTime(selectedContact.createdAt)} icon={<Clock3 size={16} />} />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Met status</div>
                  <div style={{ display: 'inline-flex', padding: '4px', borderRadius: '999px', backgroundColor: '#f1f5f9', gap: '4px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleMetStateChange(true)}
                      disabled={updatingMetState}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '999px',
                        border: 'none',
                        backgroundColor: selectedContact.hasMet ? '#16a34a' : 'transparent',
                        color: selectedContact.hasMet ? 'white' : '#475569',
                        cursor: updatingMetState ? 'not-allowed' : 'pointer',
                        opacity: updatingMetState ? 0.65 : 1,
                        fontWeight: 600
                      }}
                    >
                      Met
                    </button>
                    <button
                      onClick={() => handleMetStateChange(false)}
                      disabled={updatingMetState}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '999px',
                        border: 'none',
                        backgroundColor: !selectedContact.hasMet ? '#2563eb' : 'transparent',
                        color: !selectedContact.hasMet ? 'white' : '#475569',
                        cursor: updatingMetState ? 'not-allowed' : 'pointer',
                        opacity: updatingMetState ? 0.65 : 1,
                        fontWeight: 600
                      }}
                    >
                      Haven't met
                    </button>
                  </div>
                </div>

                {selectedContact.description && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Description</div>
                    <div style={{ padding: '12px 14px', borderRadius: '12px', backgroundColor: '#f8fafc', color: '#0f172a', minHeight: '44px', whiteSpace: 'pre-wrap' }}>
                      {selectedContact.description}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f172a', fontSize: '15px', fontWeight: 600 }}>
                    <Link2 size={16} /> Lead Gen Leads
                  </div>
                  {(selectedContact.leadGenLeads || []).length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>No linked Lead Gen leads.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {selectedContact.leadGenLeads.map((lead) => (
                        <button
                          key={lead.id}
                          onClick={() => navigate(`/real-estate/lead-gen?leadId=${lead.id}`)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '12px',
                            alignItems: 'center',
                            width: '100%',
                            padding: '12px 14px',
                            borderRadius: '12px',
                            border: '1px solid #dbeafe',
                            backgroundColor: '#f8fbff',
                            color: '#0f172a',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 600 }}>{lead.label || `Lead #${lead.id}`}</div>
                            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>
                              Status: {lead.status || 'N/A'}
                            </div>
                            {lead.ownerName && (
                              <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>
                                Owner link: {lead.ownerName}
                              </div>
                            )}
                          </div>
                          <div style={{ color: '#2563eb', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Open Lead
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f172a', fontSize: '15px', fontWeight: 600 }}>
                    <Link2 size={16} /> Selling Properties
                  </div>
                  {(selectedContact.sellingProperties || []).length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>No linked selling properties.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {selectedContact.sellingProperties.map((property, index) => (
                        <button
                          key={`selling-property-${property.id}-${property.relationType}-${property.contactId || property.personName || index}`}
                          onClick={() => navigate(`/selling/${property.id}`)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '12px',
                            alignItems: 'center',
                            width: '100%',
                            padding: '12px 14px',
                            borderRadius: '12px',
                            border: '1px solid #fed7aa',
                            backgroundColor: '#fff7ed',
                            color: '#0f172a',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 600 }}>{property.label || `Property #${property.id}`}</div>
                            <div style={{ color: '#9a3412', fontSize: '12px', marginTop: '4px' }}>
                              Relation: {property.relationType || 'linked'}
                            </div>
                            {property.personName && (
                              <div style={{ color: '#c2410c', fontSize: '12px', marginTop: '4px' }}>
                                As: {property.personName}
                              </div>
                            )}
                            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>
                              Status: {property.status || 'N/A'}
                            </div>
                          </div>
                          <div style={{ color: '#c2410c', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Open Property
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f172a', fontSize: '15px', fontWeight: 600 }}>
                    <Link2 size={16} /> Sources
                  </div>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {(selectedContact.sources || []).length === 0 ? (
                      <div style={{ color: '#94a3b8', fontSize: '13px' }}>No sources attached.</div>
                    ) : (
                      selectedContact.sources.map((source) => {
                        const properties = source.sourceSnapshot?.metadata?.properties || source.sourceSnapshot?.metadata?.properties || source.sourceSnapshot?.properties || [];
                        return (
                          <div key={`${source.sourceType}-${source.sourceId}`} style={{ padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#fcfcfd' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                              <div style={{ color: '#0f172a', fontWeight: 600, fontSize: '14px' }}>{source.sourceLabel || `${source.sourceType} #${source.sourceId}`}</div>
                              <div style={{ color: '#64748b', fontSize: '12px' }}>{source.sourceType}</div>
                            </div>
                            <div style={{ color: '#475569', fontSize: '12px', marginTop: '6px' }}>
                              Linked: {formatDateTime(source.updatedAt)}
                            </div>
                            {properties.length > 0 && (
                              <div style={{ marginTop: '8px' }}>
                                <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Properties</div>
                                <div style={{ display: 'grid', gap: '4px' }}>
                                  {properties.map((property, index) => (
                                    <div key={`${source.sourceType}-${source.sourceId}-property-${index}`} style={{ color: '#0f172a', fontSize: '12px' }}>
                                      {property.label || property.idSign || 'Unknown property'}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', color: '#0f172a', fontSize: '18px', fontWeight: 600 }}>
              <Clock3 size={18} /> Activity History
            </div>

            {!selectedContact ? (
              <div style={{ color: '#94a3b8', fontSize: '13px' }}>Select a contact to see history.</div>
            ) : activity.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '13px' }}>No activity recorded yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {activity.map((entry) => (
                  <div key={entry.id} style={{ padding: '14px 16px', borderRadius: '12px', border: entry.eventType === 'note_added' ? '1px solid #fde68a' : '1px solid #e2e8f0', backgroundColor: entry.eventType === 'note_added' ? '#fffbeb' : '#fcfcfd' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ color: '#0f172a', fontWeight: 600, fontSize: '14px' }}>{entry.description || entry.eventType}</div>
                      <div style={{ color: '#64748b', fontSize: '12px' }}>{formatDateTime(entry.createdAt)}</div>
                    </div>
                    <div style={{ color: '#475569', fontSize: '12px', marginTop: '6px' }}>
                      Source: {formatActivitySource(entry.sourceType)}{entry.sourceId ? ` #${entry.sourceId}` : ''}
                    </div>
                    {entry.fieldName && entry.eventType !== 'note_added' && (
                      <div style={{ color: '#334155', fontSize: '12px', marginTop: '6px' }}>
                        Field: {formatActivityField(entry.fieldName)}
                      </div>
                    )}
                    {entry.fieldName && entry.eventType !== 'note_added' && (
                      <div style={{ color: '#334155', fontSize: '12px', marginTop: '6px', display: 'grid', gap: '4px' }}>
                        <div>Before: {formatActivityValue(entry.oldValue)}</div>
                        <div>After: {formatActivityValue(entry.newValue)}</div>
                      </div>
                    )}
                    {entry.eventType === 'note_added' && entry.eventPayload?.note && (
                      <div style={{ color: '#92400e', fontSize: '12px', marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                        Note: {entry.eventPayload.note}
                      </div>
                    )}
                    <div style={{ color: '#64748b', fontSize: '12px', marginTop: '6px' }}>
                      By: {entry.createdBy || 'System'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {isAddModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 }}>
          <div style={{ width: '100%', maxWidth: '540px', backgroundColor: 'white', borderRadius: '18px', padding: '22px', boxShadow: '0 20px 45px rgba(15, 23, 42, 0.22)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>Add Contact</div>
                <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>Create a manual contact entry directly from the Contacts page.</div>
              </div>
              <button onClick={() => setIsAddModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '14px' }}>
              <ModalField label="Full Name">
                <input value={newContact.fullName} onChange={(event) => setNewContact((prev) => ({ ...prev, fullName: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
              </ModalField>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <ModalField label="Date of Birth">
                  <input type="date" value={newContact.dateOfBirth} onChange={(event) => setNewContact((prev) => ({ ...prev, dateOfBirth: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                </ModalField>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <ModalField label="Email">
                  <input value={newContact.email} onChange={(event) => setNewContact((prev) => ({ ...prev, email: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                </ModalField>
                <ModalField label="Phone">
                  <input value={newContact.phone} onChange={(event) => setNewContact((prev) => ({ ...prev, phone: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                </ModalField>
              </div>
              <ModalField label="Address">
                <input value={newContact.address} onChange={(event) => setNewContact((prev) => ({ ...prev, address: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
              </ModalField>
              <div style={{ display: 'inline-flex', padding: '4px', borderRadius: '999px', backgroundColor: '#f1f5f9', gap: '4px', alignItems: 'center' }}>
                <button
                  onClick={() => setNewContact((prev) => ({ ...prev, hasMet: true }))}
                  type="button"
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: 'none',
                    backgroundColor: newContact.hasMet ? '#16a34a' : 'transparent',
                    color: newContact.hasMet ? 'white' : '#475569',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Met
                </button>
                <button
                  onClick={() => setNewContact((prev) => ({ ...prev, hasMet: false }))}
                  type="button"
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: 'none',
                    backgroundColor: !newContact.hasMet ? '#2563eb' : 'transparent',
                    color: !newContact.hasMet ? 'white' : '#475569',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Haven't met
                </button>
              </div>
              <ModalField label="Description">
                <textarea value={newContact.description} onChange={(event) => setNewContact((prev) => ({ ...prev, description: event.target.value }))} rows={3} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', resize: 'vertical' }} />
              </ModalField>
              <ModalField label="Notes">
                <textarea value={newContact.notes} onChange={(event) => setNewContact((prev) => ({ ...prev, notes: event.target.value }))} rows={4} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', resize: 'vertical' }} />
              </ModalField>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setIsAddModalOpen(false)} style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#334155', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreateContact} disabled={savingContact} style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#0f766e', color: 'white', cursor: savingContact ? 'not-allowed' : 'pointer', opacity: savingContact ? 0.6 : 1 }}>
                {savingContact ? 'Saving...' : 'Create Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 }}>
          <div style={{ width: '100%', maxWidth: '540px', backgroundColor: 'white', borderRadius: '18px', padding: '22px', boxShadow: '0 20px 45px rgba(15, 23, 42, 0.22)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>Edit Contact</div>
                <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>Update the contact's full profile, description, and notes.</div>
              </div>
              <button onClick={() => setIsEditModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '14px' }}>
              <ModalField label="Full Name">
                <input value={editContact.fullName} onChange={(event) => setEditContact((prev) => ({ ...prev, fullName: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
              </ModalField>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <ModalField label="Date of Birth">
                  <input type="date" value={editContact.dateOfBirth} disabled style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#f8fafc', color: '#64748b', cursor: 'not-allowed' }} />
                </ModalField>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <ModalField label="Email">
                  <input value={editContact.email} onChange={(event) => setEditContact((prev) => ({ ...prev, email: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                </ModalField>
                <ModalField label="Phone">
                  <input value={editContact.phone} onChange={(event) => setEditContact((prev) => ({ ...prev, phone: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
                </ModalField>
              </div>
              <ModalField label="Address">
                <input value={editContact.address} onChange={(event) => setEditContact((prev) => ({ ...prev, address: event.target.value }))} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px' }} />
              </ModalField>
              <ModalField label="Description">
                <textarea value={editContact.description} onChange={(event) => setEditContact((prev) => ({ ...prev, description: event.target.value }))} rows={3} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', resize: 'vertical' }} />
              </ModalField>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setIsEditModalOpen(false)} style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#334155', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveContact} disabled={updatingContact} style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#0f766e', color: 'white', cursor: updatingContact ? 'not-allowed' : 'pointer', opacity: updatingContact ? 0.6 : 1 }}>
                {updatingContact ? 'Saving...' : 'Save Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isNoteModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 }}>
          <div style={{ width: '100%', maxWidth: '520px', backgroundColor: 'white', borderRadius: '18px', padding: '22px', boxShadow: '0 20px 45px rgba(15, 23, 42, 0.22)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>Add Note</div>
                <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>This note will appear in the contact activity history.</div>
              </div>
              <button onClick={() => setIsNoteModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            <ModalField label="Note">
              <textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} rows={5} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', resize: 'vertical' }} />
            </ModalField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setIsNoteModalOpen(false)} style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#334155', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddNote} disabled={addingNote} style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#a16207', color: 'white', cursor: addingNote ? 'not-allowed' : 'pointer', opacity: addingNote ? 0.6 : 1 }}>
                {addingNote ? 'Saving...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;